import React from 'react';
import { useSelector } from 'react-redux';
import { Pax } from 'gql/autogen/Pax';
import { getCounterProps, getCounterState } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxUtils';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { ElementEventIds } from '@agoda/analytics-messages-definition-activities';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { PaxEventFields } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxEventsMapping';
import { PaxRequirement } from 'gql/autogen/PaxRequirement';
import { PaxCounter } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/PaxCounter';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';

interface PaxCounterContainerProps {
    paxRequirements: PaxRequirement[];
    paxState: Pax[];
    setPaxState: (passengers: Pax[]) => void;
    inProgress: boolean;
    selectedDate: Date;
    availableQuantity: number;
    isAllOfferSpecSelected: boolean;
    isTotalReachedMaxPax: boolean;
    setOfferNotification: (offerNotification: Record<string, boolean>) => void;
    activityId: number | undefined;
}

const PAX_COUNTER_DELAY = 1000;

const PaxCounterContainer: React.FC<PaxCounterContainerProps> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const isCheckAvailableQuantity = useExperimentIsB(ExperimentId.ACT_4598);
    const getCounterPropsFromPaxRequirement = React.useMemo(() => getCounterProps(cms, props.paxState), [cms, props.paxState]);
    const isShowOfferNotification = isCheckAvailableQuantity && !props.isAllOfferSpecSelected;

    const handleOfferNotificationCallback = () => {
        if (isShowOfferNotification) props.setOfferNotification({ [OfferNotificationType.OptionSpec]: true });
    };

    // Keeping track of the last event times for each eventId
    const lastEventTime = React.useRef<Map<ElementEventIds, number>>(new Map());

    const onCounterChange = (paxRequirement: PaxRequirement) => (value: number, eventIds: ElementEventIds) => {
        const currentTime = Date.now(); // Current timestamp in milliseconds
        const lastTime = lastEventTime.current.get(eventIds) || 0;

        // Check if the last event was triggered more than 1 second ago
        if (currentTime - lastTime > PAX_COUNTER_DELAY) {
            trackElementEvent<PaxEventFields>(
                eventIds,
                {},
                {
                    travel_date: getDateInYmdFormat(props.selectedDate),
                    activity_id: props.activityId,
                    action_element_value: value.toString(),
                },
            );
            lastEventTime.current.set(eventIds, currentTime); // Update the last triggered time
        }

        const counterState = getCounterState(props.paxState, paxRequirement, value);
        if (isShowOfferNotification) {
            handleOfferNotificationCallback();
            return;
        }
        props.setPaxState(counterState);
    };

    const validItems = props.paxRequirements.filter((paxRequirement) => paxRequirement.code);
    return (
        <>
            {validItems.map((paxRequirement) => (
                <PaxCounter
                    key={paxRequirement.code || ''}
                    {...getCounterPropsFromPaxRequirement(paxRequirement, props.availableQuantity)}
                    onValueChange={onCounterChange(paxRequirement)}
                    inProgress={props.inProgress}
                    isTotalReachedMaxPax={props.isTotalReachedMaxPax}
                />
            ))}
        </>
    );
};
PaxCounterContainer.displayName = 'PaxCounterContainer';

export default PaxCounterContainer;
